import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound } from "lucide-react";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async () => {
    if (!profile.name.trim() || !profile.email.trim()) {
      toast.error("Name aur email dono zaroori hain");
      return;
    }
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", profile);
      await refreshUser();
      toast.success("Profile update ho gaya");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (pwd.new_password.length < 6) {
      toast.error("Naya password kam se kam 6 characters ka hona chahiye");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Naya password aur confirm password match nahi kar rahe");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password change ho gaya");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-slate-900">Account Settings</h1>
        <p className="text-slate-500 text-sm">Apna login email, naam aur password yahan se change karein</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl p-6 mb-6" data-testid="profile-card">
        <div className="flex items-center gap-2 mb-4">
          <UserCog size={18} className="text-[#0b5cc4]" />
          <h2 className="font-semibold text-slate-800">Profile & Login Email</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              data-testid="settings-name-input"
            />
          </div>
          <div>
            <Label>Login Email (User ID)</Label>
            <Input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              data-testid="settings-email-input"
            />
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} className="btn-primary" data-testid="save-profile-btn">
            {savingProfile ? "Saving..." : "Save Profile"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl p-6" data-testid="password-card">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-[#0b5cc4]" />
          <h2 className="font-semibold text-slate-800">Change Password</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Current Password</Label>
            <Input
              type="password"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              data-testid="settings-current-password-input"
            />
          </div>
          <div>
            <Label>New Password</Label>
            <Input
              type="password"
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              data-testid="settings-new-password-input"
            />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              data-testid="settings-confirm-password-input"
            />
          </div>
          <Button onClick={savePassword} disabled={savingPwd} className="btn-primary" data-testid="save-password-btn">
            {savingPwd ? "Saving..." : "Update Password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
